// TopBarV2 — preview-only slim dark top bar matching Karine's reference mockup.
//
// Visible left-to-right:
//   📞 Text Us: (613) 850-0805  |  💬 Message Us  |
//   ✨ Welcome Gift – Receive $60 in Crown Reserve Points  |
//   🔗 Afterpay Available  |  f  ig  |  FR
//
// Each block is clickable / actionable:
//   · Text Us  → tel: link (uses sms: on touch devices when available)
//   · Message Us → opens existing ContactModal
//   · Welcome Gift → /rewards
//   · Afterpay → /afterpay
//   · Facebook + Instagram → external in new tab
//   · FR → toggles LanguageContext
//
// The visual rotating banners ("Trending Now" marquee etc.) live BELOW
// this bar in HomePage.jsx — they are intentionally NOT inside the top
// strip so the strip stays slim and scannable on every page load.

import { Phone, MessageSquare, Sparkles, Link as LinkIcon, Facebook, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { openContactModal } from "@/components/ContactModal";

const Divider = () => (
  <span aria-hidden="true" className="hidden md:inline-block w-px h-4 bg-amber-100/15 mx-4" />
);

export default function TopBarV2() {
  const { language, setLanguage } = useLanguage();
  const isFr = language === "fr";

  return (
    <div
      className="w-full bg-[#0E0B07] text-zinc-100 text-[12px] font-medium"
      data-testid="top-bar-v2"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-between gap-y-2">
        {/* LEFT cluster — contact + offer */}
        <div className="flex flex-wrap items-center">
          <a
            href="sms:+16138500805"
            className="flex items-center gap-2 hover:text-amber-300 transition-colors"
            data-testid="topbarv2-text"
          >
            <Phone className="w-3.5 h-3.5 text-amber-300" />
            <span>
              <span className="hidden sm:inline">{isFr ? "Textez-nous : " : "Text Us: "}</span>
              <span>(613) 850-0805</span>
            </span>
          </a>
          <Divider />
          <button
            type="button"
            onClick={() => openContactModal()}
            className="flex items-center gap-2 hover:text-amber-300 transition-colors"
            data-testid="topbarv2-message"
          >
            <MessageSquare className="w-3.5 h-3.5 text-amber-300" />
            <span>{isFr ? "Écrivez-nous" : "Message Us"}</span>
          </button>
          <Divider />
          <Link
            to="/rewards"
            className="hidden md:flex items-center gap-2 text-amber-300 hover:text-amber-200 transition-colors"
            data-testid="topbarv2-welcome-gift"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>
              {isFr ? (
                <>Cadeau de bienvenue — Recevez 60 $ en points Crown Reserve</>
              ) : (
                <>Welcome Gift — Receive $60 in <span className="font-semibold">Crown Reserve Points</span></>
              )}
            </span>
          </Link>
        </div>

        {/* RIGHT cluster — utility */}
        <div className="flex items-center">
          <Link
            to="/afterpay"
            className="hidden md:flex items-center gap-2 hover:text-amber-300 transition-colors"
            data-testid="topbarv2-afterpay"
          >
            <LinkIcon className="w-3.5 h-3.5 text-amber-300" />
            <span>{isFr ? "Afterpay disponible" : "Afterpay Available"}</span>
          </Link>
          <Divider />
          <a
            href="https://www.facebook.com/karinelauzonrn"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            className="text-zinc-300 hover:text-amber-300 transition-colors p-1"
            data-testid="topbarv2-facebook"
          >
            <Facebook className="w-4 h-4" />
          </a>
          <a
            href="https://www.instagram.com/karinelauzon.rn"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="text-zinc-300 hover:text-amber-300 transition-colors p-1 ml-1"
            data-testid="topbarv2-instagram"
          >
            <Instagram className="w-4 h-4" />
          </a>
          <Divider />
          <button
            type="button"
            onClick={() => setLanguage(isFr ? "en" : "fr")}
            className="font-bold tracking-wider text-zinc-200 hover:text-amber-300 transition-colors px-1"
            data-testid="topbarv2-lang"
            aria-label="Toggle language"
          >
            {isFr ? "EN" : "FR"}
          </button>
        </div>
      </div>
    </div>
  );
}
